import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrophy } from '@fortawesome/free-solid-svg-icons';
import { useHistory } from 'react-router-dom';
import CgRadixButton from 'modules/Common/ui/CgRadixButton';
import {
  useFetchGuidingChecklists,
  useDismissChecklist,
  useTickChecklistItem,
} from '../hooks/useGuidingChecklists';
import GuidingChecklistItemModal from './GuidingChecklistItemModal';
import OnboardingGuidedPath from './OnboardingGuidedPath';
import OnboardingVideoLightbox from './OnboardingVideoLightbox';

/**
 * GuidingChecklistPanel — the declarant onboarding path (design `3b`).
 *
 * Two states: the guided path, then complete. There is one path — get the
 * year's report filed — so the panel opens straight onto it, with no goal
 * question in front of it (D18 in
 * docs/features/onboarding-guided-path/00-OVERVIEW.md). Ticking, dismissal and
 * the detail modal are unchanged from the previous panel.
 *
 * Rendered on BOTH declarant surfaces — Home's grid cell and the floating
 * widget's docked column — so the path is drawn once and reads the same in
 * either. It fills whatever height its container gives it (`h-full`), and only
 * the rail inside `OnboardingGuidedPath` scrolls; the panel itself does not.
 * `GuidingChecklistContent` still serves the operator overview's flat list.
 */
const GuidingChecklistPanel = () => {
  const { t } = useTranslation('onboarding');
  const history = useHistory();
  const { data: checklists, isLoading, isError } = useFetchGuidingChecklists();
  const dismissMutation = useDismissChecklist();
  const tickMutation = useTickChecklistItem();
  const [selectedItem, setSelectedItem] = useState(null);
  // The step whose walkthrough is playing. Held here rather than inside the
  // rail so it survives a refetch re-rendering the steps under it, and so both
  // surfaces get the overlay from the one place that draws the panel.
  const [videoItem, setVideoItem] = useState(null);

  const activeChecklist = useMemo(
    () => checklists?.find((c) => !c.dismissed) ?? null,
    [checklists],
  );

  const items = activeChecklist?.items ?? [];

  const openItem = (item) => setSelectedItem(item);

  // Same manual-tick path as GuidingChecklistContent: most items auto-tick from
  // backend triggers, but the modal keeps the manual escape hatch.
  const handleTick = (itemKey) => {
    tickMutation.mutate(
      { checklistSlug: activeChecklist.slug, itemKey },
      { onSuccess: () => setSelectedItem(null) },
    );
  };

  if (isLoading) {
    return (
      // Heights mirror the loaded panel so the swap doesn't jump.
      <div className="h-full flex flex-col justify-center gap-2.5 animate-pulse">
        <div className="h-[28px] bg-slate-100 rounded-lg w-1/3 self-end" />
        <div className="h-[132px] bg-slate-100 rounded-xl" />
        <div className="h-[24px] bg-slate-100 rounded-lg w-2/3" />
      </div>
    );
  }

  // Graceful degradation — matches GuidingChecklistContent's contract. The page
  // only shows this tab while a checklist is active, so this is belt-and-braces.
  if (isError || !activeChecklist) return null;

  const hasPending = items.some((item) => !item.completed);

  // All done: the tab disappears on the next fetch (hasActiveChecklist flips
  // false once isComplete), so this state is the moment of completion.
  if (!hasPending) {
    return (
      <div className="h-full flex flex-col items-center justify-center text-center px-6">
        <div className="w-12 h-12 rounded-full bg-primaryColor/10 flex items-center justify-center mb-3">
          <FontAwesomeIcon
            icon={faTrophy}
            className="text-primaryColor text-xl"
          />
        </div>
        <p className="text-base font-bold text-slate-900 m-0">
          {t('checklist.complete.title', '100% report-ready')}
        </p>
        <p className="text-[13px] text-slate-500 leading-relaxed mt-1.5 mb-0 max-w-[320px]">
          {t('checklist.complete.body', {
            count: items.length,
            defaultValue:
              'All {{count}} step is done — everything the CBAM declaration needs is in place.',
            defaultValue_other:
              'All {{count}} steps are done — everything the CBAM declaration needs is in place.',
          })}
        </p>
        <div className="flex gap-2 mt-3.5">
          <CgRadixButton
            variant="primary"
            onClick={() => history.push('/reports')}
          >
            {t('checklist.complete.goToReports', 'Go to reports')}
          </CgRadixButton>
          {activeChecklist.isOptional && (
            <CgRadixButton
              variant="secondary"
              loading={dismissMutation.isPending}
              onClick={() => dismissMutation.mutate({ checklistId: activeChecklist._id })}
            >
              {t('guidedPath.hideChecklist', 'Hide checklist')}
            </CgRadixButton>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="h-full min-h-0">
      <OnboardingGuidedPath
        items={items}
        onNavigate={(route) => history.push(route)}
        onOpenItem={openItem}
        onPlayVideo={setVideoItem}
        onDismiss={() => dismissMutation.mutate({ checklistId: activeChecklist._id })}
        isDismissing={dismissMutation.isPending}
        canDismiss={activeChecklist.isOptional}
      />

      <GuidingChecklistItemModal
        show={!!selectedItem}
        onHide={() => setSelectedItem(null)}
        item={selectedItem}
        onTick={handleTick}
        isTickLoading={tickMutation.isPending}
      />

      {/* The walkthrough plays over the page rather than inside a modal — see
          OnboardingVideoLightbox. Mounted only while playing, so the iframe is
          created on the click and torn down (stopping playback) on close. */}
      {videoItem && (
        <OnboardingVideoLightbox
          videoUrl={videoItem.videoUrl}
          title={videoItem.name}
          onClose={() => setVideoItem(null)}
        />
      )}
    </div>
  );
};

export default GuidingChecklistPanel;
